import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  DatePicker,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { capabilityApi, userApi } from '../api';
import { useAsync } from './useAsync';
import type { UserDetail } from '../types';
import { WORKSPACE_ROLE_LABEL, capabilityLabel } from '../types';

/** The user a grant is being written for. Fixed when opened from a user drawer. */
export type GrantSubject = Pick<UserDetail, 'id' | 'email' | 'workspaces'>;

/**
 * Grant a capability without a request — `POST /capabilities/grants`.
 *
 * The requests tab on Accesses covers the normal path: the customer asks, staff
 * approve. This is the other one, which the console previously had no screen
 * for — staff agreeing to something in a call or a Slack thread, where no
 * request row exists to approve. That was the last reason to reach for the API
 * by hand, and a grant made by hand is a grant with no reason recorded.
 *
 * Mount it only while it should be open (the parent renders it conditionally),
 * so every open starts on a clean form rather than the last one's leftovers.
 *
 * Two things the backend enforces that this form makes visible rather than
 * letting the user discover by rejection:
 *
 *  * A grant needs workspace membership — `grant_directly` refuses otherwise,
 *    because a grant in a workspace the user is not in confers nothing. So the
 *    workspace picker offers memberships, never the full workspace list.
 *  * Re-granting edits the existing row rather than adding a second one. It
 *    overwrites `expires_at` outright, so an existing expiry is prefilled here:
 *    submitting a blank date on a live grant would quietly make it permanent.
 */
export default function GrantCapabilityModal({
  subject,
  onClose,
  onGranted,
}: {
  /** Omit to pick the user here; pass one when the caller already knows them. */
  subject?: GrantSubject;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [userId, setUserId] = useState<number | undefined>(subject?.id);
  const [workspaceId, setWorkspaceId] = useState<number | undefined>();
  const [capability, setCapability] = useState<string | undefined>();
  const [expiry, setExpiry] = useState<dayjs.Dayjs | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // The whitelist comes from the server so this console never hard-codes which
  // capabilities may be granted — adding one backend-side shows up here.
  const grantable = useAsync(() => capabilityApi.grantable(), []);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // The picker spans every organization, so it searches server-side rather than
  // filtering a fetched page. Debounced because `onSearch` fires per keystroke.
  useEffect(() => {
    if (subject) return;
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search, subject]);

  const candidates = useAsync(
    () =>
      subject ? Promise.resolve(null) : userApi.list({ q: query || undefined, per_page: 20 }),
    [subject, query],
  );

  // Memberships decide the workspace options, so a user picked here needs their
  // detail loaded. A subject passed in already carries them.
  const picked = useAsync(
    () => (subject || !userId ? Promise.resolve(null) : userApi.get(userId)),
    [subject, userId],
  );
  const workspaces = subject?.workspaces ?? picked.data?.workspaces ?? [];

  // Existing grants for this user, so the form can say what a submit will do
  // instead of the server saying it afterwards.
  const existing = useAsync(
    () =>
      userId ? capabilityApi.grants({ user_id: userId, per_page: 100 }) : Promise.resolve(null),
    [userId],
  );

  // `is_live` for the label, because that is what the user actually holds right
  // now — an un-revoked row with a past expiry confers nothing.
  const liveInWorkspace = useMemo(
    () =>
      new Set(
        (existing.data?.items ?? [])
          .filter((g) => g.workspace_id === workspaceId && g.is_live)
          .map((g) => g.capability),
      ),
    [existing.data, workspaceId],
  );

  // `!revoked_at` for the row this submit would touch, because that is the
  // condition `grant_directly` uses to decide edit-vs-insert. An expired grant
  // gets revived rather than duplicated, which is worth saying out loud.
  const match = useMemo(
    () =>
      (existing.data?.items ?? []).find(
        (g) =>
          g.workspace_id === workspaceId && g.capability === capability && !g.revoked_at,
      ) ?? null,
    [existing.data, workspaceId, capability],
  );

  // Prefill from the grant that would be edited. Only `expires_at`: the backend
  // keeps the old reason when a blank one is sent, so an empty box here means
  // "leave the reason as it was", which is not true of the date.
  useEffect(() => {
    setExpiry(match?.expires_at ? dayjs(match.expires_at) : null);
  }, [match?.id, match?.expires_at]);

  const email = subject?.email ?? picked.data?.email;
  const membership = workspaces.find((w) => w.workspace_id === workspaceId);

  async function submit() {
    if (!userId || !workspaceId || !capability) return;
    setSubmitting(true);
    try {
      await capabilityApi.createGrant({
        user_id: userId,
        workspace_id: workspaceId,
        capability,
        expires_at: expiry ? expiry.toISOString() : undefined,
        reason: reason.trim() || undefined,
      });
      message.success(
        match ? 'Grant updated. Effective immediately.' : 'Granted. Effective immediately.',
      );
      onGranted();
      onClose();
    } catch {
      /* toast already shown by the axios layer */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Grant a capability"
      open
      onCancel={onClose}
      onOk={submit}
      okText={match ? 'Update grant' : 'Grant'}
      okButtonProps={{ disabled: !userId || !workspaceId || !capability }}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
        For access agreed outside the request queue. It lands in the same place an
        approved request does &mdash; one workspace, an optional expiry, and a
        recorded reason.
      </Typography.Paragraph>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {!subject && (
          <div>
            <Typography.Text strong>User</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              placeholder="Search by email or name"
              value={userId}
              onChange={(value) => {
                setUserId(value);
                setWorkspaceId(undefined);
              }}
              showSearch
              // Server-side search, so antd must not also filter the results.
              filterOption={false}
              onSearch={setSearch}
              loading={candidates.loading}
              notFoundContent={candidates.loading ? 'Searching…' : 'No users match'}
              options={(candidates.data?.items ?? []).map((u) => ({
                value: u.id,
                label: `${u.first_name} ${u.last_name} — ${u.email}`,
              }))}
            />
          </div>
        )}

        {subject && (
          <div>
            <Typography.Text strong>User</Typography.Text>
            <div className="mono" style={{ marginTop: 6 }}>
              {subject.email}
            </div>
          </div>
        )}

        {userId && !picked.loading && workspaces.length === 0 && (
          <Alert
            type="warning"
            showIcon
            message="No workspace membership"
            description={
              <>
                A grant applies inside one workspace, so there is nowhere to put
                one until {email ?? 'this user'} belongs to a workspace. Add them
                on the Workspaces screen first.
              </>
            }
          />
        )}

        <div>
          <Typography.Text strong>Workspace</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            placeholder={userId ? 'Where the grant applies' : 'Pick a user first'}
            disabled={!userId || workspaces.length === 0}
            loading={picked.loading}
            value={workspaceId}
            onChange={setWorkspaceId}
            showSearch
            optionFilterProp="label"
            options={workspaces.map((w) => ({
              value: w.workspace_id,
              label: `${w.workspace_name} (${w.workspace_slug}) — ${
                WORKSPACE_ROLE_LABEL[w.role] ?? w.role
              }`,
            }))}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Only workspaces this user belongs to. Their role there is shown
            because it may already carry what you are about to grant.
          </Typography.Text>
        </div>

        <div>
          <Typography.Text strong>Capability</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            placeholder="What they may do"
            value={capability}
            onChange={setCapability}
            loading={grantable.loading}
            options={(grantable.data?.capabilities ?? []).map((value) => ({
              value,
              label: liveInWorkspace.has(value)
                ? `${capabilityLabel(value)} — already granted here`
                : capabilityLabel(value),
            }))}
          />
        </div>

        {match && (
          <Alert
            type="info"
            showIcon
            message={
              match.is_live ? 'This grant already exists' : 'An expired grant exists'
            }
            description={
              <>
                {email}{' '}
                {match.is_live
                  ? 'already holds'
                  : 'held, until it expired,'}{' '}
                {capabilityLabel(match.capability)} in{' '}
                <span className="mono">{match.workspace_slug}</span>. Submitting
                edits that grant rather than adding a second one: the expiry below
                replaces the current one, and a blank reason keeps the reason
                already recorded.
              </>
            }
          />
        )}

        <div>
          <Typography.Text strong>Expires</Typography.Text>
          <DatePicker
            style={{ width: '100%', marginTop: 6 }}
            value={expiry}
            onChange={setExpiry}
            disabledDate={(d) => d.isBefore(dayjs(), 'day')}
            placeholder="No expiry"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Leave empty for no expiry. For a one-off need a date is safer &mdash;
            it&apos;s the thing the old per-user flag could never do.
          </Typography.Text>
        </div>

        <div>
          <Typography.Text strong>Reason</Typography.Text>
          <Input.TextArea
            rows={3}
            style={{ marginTop: 6 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
            placeholder="Why this is being granted — shown on the grants list"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Worth writing: nothing else records why this exists, since there is no
            request behind it.
          </Typography.Text>
        </div>

        {membership && capability && !match && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Grants <Tag color="gold">{capabilityLabel(capability)}</Tag> in{' '}
            <span className="mono">{membership.workspace_slug}</span> only. Other
            workspaces this user belongs to are unaffected.
          </Typography.Text>
        )}
      </Space>
    </Modal>
  );
}
